import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { rememberAuthRedirect, trackFunnelEvent } from "@/lib/funnelEvents";

type ModalidadeBanhoTosa = "banho" | "tosa" | "banho_e_tosa";
type PackageValidityUnit = "day" | "week" | "month";
type SimpleExtraKey = "puppy" | "special_care" | "medication" | "transport";

type PricingRule = {
  enabled?: boolean;
  type?: "fixed" | "percent";
  value?: number;
  unit_minutes?: number;
};

type ServicePackage = {
  id: string;
  name: string;
  units: number;
  price: number;
  validity_quantity: number;
  validity_unit: PackageValidityUnit;
  validity_days?: number;
  variant_key?: ModalidadeBanhoTosa | null;
};

type CustomExtra = {
  id: string;
  code: string;
  name: string;
  enabled: boolean;
  type: "fixed" | "percent";
  value: number;
};

type PricingConfig = {
  packages?: ServicePackage[];
  extras?: CustomExtra[];
  extra_pet?: PricingRule;
  holiday?: PricingRule;
  weekend?: PricingRule;
  night?: PricingRule;
  puppy?: PricingRule;
  special_care?: PricingRule;
  medication?: PricingRule;
  transport?: PricingRule;
  extra_duration?: PricingRule;
  distance?: { enabled?: boolean; included_km?: number; price_per_km?: number; minimum_fare?: number };
  waiting?: { enabled?: boolean; free_minutes?: number; price_per_minute?: number };
  extra_stop?: { enabled?: boolean; value?: number };
  round_trip?: { enabled?: boolean; value?: number };
  long_stay_discounts?: Array<{ min_units?: number; discount_percent?: number }>;
};

type Pet = {
  id: number;
  nome: string;
};

type TimeSlot = {
  hora_inicio: string;
  hora_fim: string;
  preco?: number;
};

type QuoteLineItem = {
  code?: string;
  label?: string;
  amount?: number;
};

type PricingQuote = {
  tutor_total?: number;
  tutor_fee?: number;
  service_amount?: number;
  line_items?: QuoteLineItem[];
};

const COLORS = {
  primary: "#FE5668",
  success: "#64A002",
};

const BATH_OPTIONS: Array<{ key: ModalidadeBanhoTosa; label: string }> = [
  { key: "banho", label: "Só banho" },
  { key: "tosa", label: "Só tosa" },
  { key: "banho_e_tosa", label: "Banho e tosa" },
];

const SIMPLE_OPTIONS: Array<{ key: SimpleExtraKey; label: string; description: string }> = [
  { key: "puppy", label: "Filhote", description: "Cuidado adicional para filhotes." },
  { key: "special_care", label: "Cuidados especiais", description: "Necessidades específicas informadas ao anfitrião." },
  { key: "medication", label: "Administração de medicamento", description: "Medicação durante o atendimento." },
  { key: "transport", label: "Busca ou entrega", description: "Transporte do pet quando oferecido pelo anfitrião." },
];

function numberValue(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function money(value: unknown) {
  return numberValue(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function toYmd(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function diffDays(start: string, end: string) {
  if (!start || !end) return 1;
  const a = new Date(`${start}T00:00:00`);
  const b = new Date(`${end}T00:00:00`);
  const days = Math.round((b.getTime() - a.getTime()) / 86400000);
  return Math.max(1, days);
}

function diffHours(start?: string | null, end?: string | null) {
  if (!start || !end) return 1;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const minutes = eh * 60 + em - (sh * 60 + sm);
  return Math.max(1, minutes / 60);
}

function formatRule(rule?: PricingRule | null) {
  if (!rule?.enabled) return null;
  const value = Math.max(0, numberValue(rule.value));
  return rule.type === "percent" ? `+ ${value}%` : `+ ${money(value)}`;
}

function packageValidityLabel(pkg: ServicePackage) {
  const amount = Math.max(1, numberValue(pkg.validity_quantity, 1));
  const unit = pkg.validity_unit || "month";
  const labels: Record<PackageValidityUnit, [string, string]> = {
    day: ["dia", "dias"],
    week: ["semana", "semanas"],
    month: ["mês", "meses"],
  };
  return `${amount} ${labels[unit][amount === 1 ? 0 : 1]}`;
}

function bathLabel(value?: ModalidadeBanhoTosa | null) {
  return BATH_OPTIONS.find((item) => item.key === value)?.label ?? null;
}

function isPastSlot(date: string, time: string) {
  if (!date) return false;
  const now = new Date();
  const selected = new Date(`${date}T${time}:00`);
  return selected.getTime() <= now.getTime();
}

function responseData(payload: any) {
  if (!payload) return {};
  if (payload.servico_id || payload.servico || payload.tipo_servico) return payload;
  return payload.item ?? payload.data ?? payload.servico ?? payload;
}

function appendArray(payload: URLSearchParams, key: string, values: string[]) {
  values.forEach((value) => payload.append(`${key}[]`, value));
}

export default function ServiceBookingEnhancer() {
  const { id } = useParams();
  const location = useLocation();
  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const userId = (window as any).__AUTH_ID__ ?? null;
  const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "";

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(initialParams.get("entrada") || "");
  const [endDate, setEndDate] = useState(initialParams.get("saida") || initialParams.get("entrada") || "");
  const [modality, setModality] = useState<ModalidadeBanhoTosa | "">(() => {
    const value = initialParams.get("modalidade");
    return BATH_OPTIONS.some((item) => item.key === value) ? (value as ModalidadeBanhoTosa) : "";
  });
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [selectedExtraIds, setSelectedExtraIds] = useState<string[]>([]);
  const [simpleSelections, setSimpleSelections] = useState<Record<SimpleExtraKey, boolean>>({
    puppy: false,
    special_care: false,
    medication: false,
    transport: false,
  });
  const [extraDurationMinutes, setExtraDurationMinutes] = useState(0);
  const [extraStopCount, setExtraStopCount] = useState(0);
  const [roundTrip, setRoundTrip] = useState(false);

  const [pets, setPets] = useState<Pet[]>([]);
  const [petsLoading, setPetsLoading] = useState(false);
  const [selectedPetIds, setSelectedPetIds] = useState<number[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);

  const [quote, setQuote] = useState<PricingQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    const interceptReservationClick = (event: MouseEvent) => {
      if (!location.pathname.startsWith("/servico/")) return;
      const target = event.target as HTMLElement | null;
      const button = target?.closest("button");
      if (!button || button.closest("[data-allepets-enhanced-booking]")) return;
      const text = String(button.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      if (text !== "solicitar reserva" && text !== "reservar") return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      trackFunnelEvent("reservation_form_open", {
        source: "enhanced_pricing_modal",
        authenticated: Boolean(userId),
      });
      setOpen(true);
    };

    document.addEventListener("click", interceptReservationClick, true);
    return () => document.removeEventListener("click", interceptReservationClick, true);
  }, [location.pathname, userId]);

  useEffect(() => {
    if (!open || !id) return;
    const controller = new AbortController();
    const params = new URLSearchParams();
    if (startDate) params.set("entrada", startDate);
    if (endDate) params.set("saida", endDate || startDate);
    const query = params.toString();

    setDetailLoading(true);
    setDetailError(null);
    fetch(`/api/servico/${id}${query ? `?${query}` : ""}`, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => setDetail(responseData(payload)))
      .catch((error) => {
        if (error?.name !== "AbortError") setDetailError("Não foi possível atualizar os dados da reserva.");
      })
      .finally(() => setDetailLoading(false));

    return () => controller.abort();
  }, [open, id, startDate, endDate]);

  useEffect(() => {
    if (!open || !userId) return;
    let alive = true;
    setPetsLoading(true);
    fetch("/tutor/pets", {
      credentials: "include",
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (!alive) return;
        setPets(Array.isArray(payload?.pets) ? payload.pets : []);
      })
      .catch(() => {
        if (alive) setPets([]);
      })
      .finally(() => {
        if (alive) setPetsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, userId]);

  const service = detail?.servico ?? detail ?? {};
  const serviceType = detail?.tipo_servico ?? service?.tipo_servico ?? {};
  const pricingConfig: PricingConfig = service?.pricing_config ?? detail?.pricing_config ?? {};
  const serviceId = Number(detail?.servico_id ?? service?.id ?? id ?? 0);
  const hostId = Number(detail?.anfitriao?.id ?? service?.anfitriao?.id ?? service?.user_id ?? 0);
  const serviceName = String(serviceType?.nome ?? detail?.nome ?? "Serviço");
  const hostName = String(detail?.host?.nome ?? detail?.anfitriao?.name ?? service?.anfitriao?.name ?? "Anfitrião");
  const pricingType = String(serviceType?.tipo_precificacao ?? "diaria").toLowerCase();
  const slug = String(serviceType?.slug ?? "").toLowerCase();
  const bathRequired = Boolean(detail?.exige_modalidade) || (serviceName.toLowerCase().includes("banho") && serviceName.toLowerCase().includes("tosa"));
  const hourly = pricingType === "horaria" || ["petsitter", "passeio", "veterinario"].includes(slug);
  const period = pricingType === "diaria" || pricingType === "creche" || slug === "creche";

  const bathPrices = useMemo(() => ({
    banho: numberValue(detail?.modalidades_servico?.banho ?? service?.preco_banho),
    tosa: numberValue(detail?.modalidades_servico?.tosa ?? service?.preco_tosa),
    banho_e_tosa: numberValue(detail?.modalidades_servico?.banho_e_tosa ?? service?.preco_banho_tosa),
  }), [detail, service]);

  const availableBathOptions = useMemo(
    () => BATH_OPTIONS.filter((item) => bathPrices[item.key] > 0),
    [bathPrices]
  );

  const packages = useMemo<ServicePackage[]>(() => {
    const source = Array.isArray(detail?.packages)
      ? detail.packages
      : Array.isArray(pricingConfig.packages)
        ? pricingConfig.packages
        : [];
    return source
      .map((pkg: any) => ({
        id: String(pkg?.id || ""),
        name: String(pkg?.name || ""),
        units: Math.max(1, numberValue(pkg?.units, 1)),
        price: Math.max(0, numberValue(pkg?.price)),
        validity_quantity: Math.max(1, numberValue(pkg?.validity_quantity, 1)),
        validity_unit: (["day", "week", "month"].includes(String(pkg?.validity_unit)) ? pkg.validity_unit : "month") as PackageValidityUnit,
        validity_days: numberValue(pkg?.validity_days, 30),
        variant_key: BATH_OPTIONS.some((item) => item.key === pkg?.variant_key) ? pkg.variant_key : null,
      }))
      .filter((pkg: ServicePackage) => pkg.id && pkg.price > 0)
      .filter((pkg: ServicePackage) => !pkg.variant_key || bathPrices[pkg.variant_key] > 0);
  }, [detail, pricingConfig.packages, bathPrices]);

  const selectedPackage = packages.find((pkg) => pkg.id === selectedPackageId) ?? null;

  const customExtras = useMemo<CustomExtra[]>(() => {
    const source = Array.isArray(pricingConfig.extras) ? pricingConfig.extras : [];
    return source
      .map((extra: any) => ({
        id: String(extra?.id || ""),
        code: String(extra?.code || ""),
        name: String(extra?.name || extra?.code || "Adicional"),
        enabled: Boolean(extra?.enabled),
        type: extra?.type === "percent" ? "percent" : "fixed",
        value: Math.max(0, numberValue(extra?.value)),
      }))
      .filter((extra) => extra.enabled && extra.id && extra.value > 0);
  }, [pricingConfig.extras]);

  const simpleOptions = useMemo(
    () => SIMPLE_OPTIONS.filter((option) => Boolean((pricingConfig[option.key] as PricingRule | undefined)?.enabled)),
    [pricingConfig]
  );

  const autoRules = useMemo(() => {
    const items: Array<{ label: string; rule: PricingRule }> = [];
    if (pricingConfig.extra_pet?.enabled) items.push({ label: "Pet adicional", rule: pricingConfig.extra_pet });
    if (pricingConfig.weekend?.enabled) items.push({ label: "Fim de semana", rule: pricingConfig.weekend });
    if (pricingConfig.holiday?.enabled) items.push({ label: "Feriado", rule: pricingConfig.holiday });
    if (pricingConfig.night?.enabled) items.push({ label: "Horário noturno", rule: pricingConfig.night });
    return items;
  }, [pricingConfig]);

  const timeSlots = useMemo<TimeSlot[]>(() => {
    const source = Array.isArray(detail?.precos_detalhados)
      ? detail.precos_detalhados
      : Array.isArray(service?.precos_detalhados)
        ? service.precos_detalhados
        : [];
    return source
      .filter((slot: any) => slot?.hora_inicio && slot?.hora_fim && slot?.status === "disponivel")
      .map((slot: any) => ({
        hora_inicio: String(slot.hora_inicio).slice(0, 5),
        hora_fim: String(slot.hora_fim).slice(0, 5),
        preco: numberValue(slot.preco),
      }))
      .filter((slot: TimeSlot) => !startDate || !isPastSlot(startDate, slot.hora_inicio));
  }, [detail, service, startDate]);

  useEffect(() => {
    if (!hourly || !timeSlots.length) {
      if (!hourly) setSelectedSlot(null);
      return;
    }
    const stillAvailable = selectedSlot && timeSlots.some(
      (slot) => slot.hora_inicio === selectedSlot.hora_inicio && slot.hora_fim === selectedSlot.hora_fim
    );
    if (!stillAvailable) setSelectedSlot(timeSlots[0]);
  }, [hourly, timeSlots, selectedSlot]);

  useEffect(() => {
    if (!bathRequired || modality || !availableBathOptions.length) return;
    const packageVariant = selectedPackage?.variant_key;
    if (packageVariant) setModality(packageVariant);
  }, [bathRequired, modality, availableBathOptions, selectedPackage]);

  const baseUnitPrice = useMemo(() => {
    if (bathRequired) return modality ? bathPrices[modality] : 0;
    if (hourly && selectedSlot?.preco) return selectedSlot.preco;
    return numberValue(detail?.preco_exibicao ?? service?.preco_base);
  }, [bathRequired, modality, bathPrices, hourly, selectedSlot, detail, service]);

  const estimatedTotal = useMemo(() => {
    const petMultiplier = Math.max(1, selectedPetIds.length);
    if (selectedPackage) return selectedPackage.price * petMultiplier;
    if (!baseUnitPrice || !startDate) return 0;
    const units = period ? diffDays(startDate, endDate || startDate) : hourly ? diffHours(selectedSlot?.hora_inicio, selectedSlot?.hora_fim) : 1;
    return baseUnitPrice * units * petMultiplier;
  }, [selectedPackage, selectedPetIds.length, baseUnitPrice, startDate, endDate, period, hourly, selectedSlot]);

  function buildPricingPayload() {
    const payload = new URLSearchParams();
    if (csrf) payload.set("_token", csrf);
    payload.set("servico_id", String(serviceId));
    payload.set("anfitriao_id", String(hostId || ""));
    payload.set("data_inicio", startDate);
    payload.set("data_fim", endDate || startDate);
    payload.set("pet_ids", selectedPetIds.join(","));
    payload.set("preco", numberValue(baseUnitPrice).toFixed(2));
    payload.set("mensagem", "");

    if (modality) payload.set("modalidade_servico", modality);
    if (selectedPackageId) payload.set("package_id", selectedPackageId);
    if (hourly && selectedSlot) {
      payload.set("hora_inicio", `${selectedSlot.hora_inicio}:00`);
      payload.set("hora_fim", `${selectedSlot.hora_fim}:00`);
    }

    appendArray(payload, "selected_extra_ids", selectedExtraIds);
    (Object.keys(simpleSelections) as SimpleExtraKey[]).forEach((key) => {
      if (simpleSelections[key]) payload.set(key, "1");
    });
    if (extraDurationMinutes > 0) payload.set("extra_duration_minutes", String(extraDurationMinutes));
    if (extraStopCount > 0) payload.set("extra_stop_count", String(extraStopCount));
    if (roundTrip) payload.set("round_trip", "1");
    return payload;
  }

  const quoteReady = Boolean(
    open &&
    userId &&
    serviceId &&
    startDate &&
    selectedPetIds.length > 0 &&
    (!bathRequired || modality) &&
    (!hourly || selectedSlot)
  );

  useEffect(() => {
    if (!quoteReady) {
      setQuote(null);
      setQuoteError(null);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setQuoteLoading(true);
      setQuoteError(null);
      try {
        const response = await fetch("/pagamento/simular", {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
            "X-CSRF-TOKEN": csrf,
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          body: buildPricingPayload().toString(),
        });
        const payload = await response.json().catch(() => ({}));
        if (response.status === 401 || response.status === 403) {
          setQuote(null);
          return;
        }
        if (!response.ok || !payload?.quote) {
          throw new Error(payload?.message || "Não foi possível calcular o total.");
        }
        setQuote(payload.quote);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setQuote(null);
          setQuoteError(error?.message || "Não foi possível calcular o total.");
        }
      } finally {
        if (!controller.signal.aborted) setQuoteLoading(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    quoteReady,
    csrf,
    serviceId,
    hostId,
    startDate,
    endDate,
    modality,
    selectedPackageId,
    selectedExtraIds,
    simpleSelections,
    extraDurationMinutes,
    extraStopCount,
    roundTrip,
    selectedPetIds,
    hourly,
    selectedSlot,
    bathRequired,
    baseUnitPrice,
  ]);

  function togglePet(idValue: number) {
    setSelectedPetIds((current) => current.includes(idValue) ? current.filter((item) => item !== idValue) : [...current, idValue]);
  }

  function toggleCustomExtra(idValue: string) {
    setSelectedExtraIds((current) => current.includes(idValue) ? current.filter((item) => item !== idValue) : [...current, idValue]);
  }

  function changeStartDate(value: string) {
    setStartDate(value);
    setEndDate((current) => !current || current < value ? value : current);
    setSelectedSlot(null);
  }

  async function checkout() {
    setCheckoutError(null);
    if (!userId) {
      rememberAuthRedirect({ source: "enhanced_pricing_modal", target: "login", page: "service_detail" });
      window.location.href = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
      return;
    }
    if (!selectedPetIds.length) {
      setCheckoutError("Selecione pelo menos um pet.");
      return;
    }
    if (!startDate) {
      setCheckoutError("Escolha a data da reserva.");
      return;
    }
    if (bathRequired && !modality) {
      setCheckoutError("Escolha Só banho, Só tosa ou Banho e tosa.");
      return;
    }
    if (hourly && !selectedSlot) {
      setCheckoutError("Escolha um horário disponível.");
      return;
    }

    setCheckoutLoading(true);
    trackFunnelEvent("checkout_start", {
      source: "enhanced_pricing_modal",
      has_pet: true,
      has_date: true,
      package_selected: Boolean(selectedPackageId),
      extras_selected: selectedExtraIds.length + Object.values(simpleSelections).filter(Boolean).length,
    });

    try {
      const response = await fetch("/pagamento/criar", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-TOKEN": csrf,
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        body: buildPricingPayload().toString(),
      });

      if (response.status === 401) {
        window.location.href = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
        return;
      }
      if (response.status === 403) {
        window.location.href = `/completar-cadastro?next=${encodeURIComponent(location.pathname + location.search)}`;
        return;
      }

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const validation = payload?.errors
          ? Object.values(payload.errors).flat().join(" ")
          : payload?.message;
        throw new Error(validation || "Não foi possível iniciar o pagamento.");
      }
      if (payload?.checkout_url) {
        window.location.href = payload.checkout_url;
        return;
      }
      throw new Error("Pagamento criado sem URL de checkout.");
    } catch (error: any) {
      setCheckoutError(error?.message || "Erro de conexão ao iniciar o pagamento.");
    } finally {
      setCheckoutLoading(false);
    }
  }

  if (!open) return null;

  const loginUrl = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
  const registerUrl = `/register?redirect=${encodeURIComponent(location.pathname + location.search)}`;
  const today = toYmd(new Date());
  const quoteLines = Array.isArray(quote?.line_items) ? quote!.line_items! : [];
  const displayedTotal = quote?.tutor_total ?? estimatedTotal;
  const hasSelectableExtras = simpleOptions.length > 0 || customExtras.length > 0 || Boolean(pricingConfig.extra_duration?.enabled) || Boolean(pricingConfig.extra_stop?.enabled) || Boolean(pricingConfig.round_trip?.enabled);

  return (
    <div data-allepets-enhanced-booking className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/55 p-3 sm:p-5" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}>
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-xl font-extrabold text-slate-950">Monte sua reserva</h2>
            <p className="mt-1 text-sm text-slate-500">{serviceName} com {hostName}</p>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="rounded-full border px-3 py-1.5 text-sm font-semibold text-slate-600">Fechar</button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          {detailLoading && !detail && <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">Carregando opções de reserva...</div>}
          {detailError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{detailError}</div>}

          {!userId && (
            <section className="rounded-2xl border border-pink-200 bg-pink-50 p-4">
              <h3 className="font-bold text-pink-950">Entre para concluir a reserva</h3>
              <p className="mt-1 text-sm text-pink-900">Você pode consultar preços, pacotes e adicionais agora. Para selecionar seus pets e pagar, entre ou crie sua conta.</p>
              <div className="mt-3 flex gap-2">
                <a href={loginUrl} className="rounded-xl bg-white px-4 py-2 text-sm font-bold text-pink-700 ring-1 ring-pink-200">Entrar</a>
                <a href={registerUrl} className="rounded-xl px-4 py-2 text-sm font-bold text-white" style={{ backgroundColor: COLORS.primary }}>Criar conta</a>
              </div>
            </section>
          )}

          <section>
            <h3 className="text-sm font-bold text-slate-900">Quando será o serviço?</h3>
            <div className={`mt-2 grid gap-3 ${period ? "sm:grid-cols-2" : "grid-cols-1"}`}>
              <label className="text-sm font-medium text-slate-700">
                {period ? "Entrada" : "Data"}
                <input type="date" min={today} value={startDate} onChange={(event) => changeStartDate(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" />
              </label>
              {period && (
                <label className="text-sm font-medium text-slate-700">
                  Saída
                  <input type="date" min={startDate || today} value={endDate} onChange={(event) => setEndDate(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" />
                </label>
              )}
            </div>
          </section>

          {bathRequired && availableBathOptions.length > 0 && (
            <section>
              <h3 className="text-sm font-bold text-slate-900">Escolha o serviço</h3>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                {availableBathOptions.map((option) => {
                  const active = modality === option.key;
                  return (
                    <button key={option.key} type="button" onClick={() => { setModality(option.key); if (selectedPackage?.variant_key && selectedPackage.variant_key !== option.key) setSelectedPackageId(""); }} className={`rounded-xl border px-3 py-3 text-left ${active ? "border-pink-400 bg-pink-50" : "border-slate-300 bg-white"}`}>
                      <span className="block text-sm font-bold">{option.label}</span>
                      <span className="text-xs text-slate-500">{money(bathPrices[option.key])}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {packages.length > 0 && (
            <section>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Reserva avulsa ou pacote?</h3>
                <p className="mt-1 text-xs text-slate-500">O pacote mostra quantidade de usos, preço total e validade antes da compra.</p>
              </div>
              <div className="mt-2 space-y-2">
                <button type="button" onClick={() => setSelectedPackageId("")} className={`w-full rounded-xl border px-4 py-3 text-left ${!selectedPackageId ? "border-pink-400 bg-pink-50" : "border-slate-300"}`}>
                  <span className="block text-sm font-bold">Reserva avulsa</span>
                  <span className="text-xs text-slate-500">Pague somente por esta reserva.</span>
                </button>
                {packages.map((pkg) => {
                  const active = selectedPackageId === pkg.id;
                  const variant = bathLabel(pkg.variant_key);
                  return (
                    <button key={pkg.id} type="button" onClick={() => { setSelectedPackageId(pkg.id); if (pkg.variant_key) setModality(pkg.variant_key); }} className={`w-full rounded-xl border px-4 py-3 text-left ${active ? "border-pink-400 bg-pink-50" : "border-slate-300"}`}>
                      <span className="flex items-start justify-between gap-3">
                        <span>
                          <span className="block text-sm font-bold">{pkg.name || `Pacote com ${pkg.units} utilizações`}</span>
                          <span className="block text-xs text-slate-500">{pkg.units} utilizações · válido por {packageValidityLabel(pkg)}{variant ? ` · ${variant}` : ""}</span>
                        </span>
                        <strong className="whitespace-nowrap text-sm" style={{ color: COLORS.success }}>{money(pkg.price)}</strong>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {hourly && startDate && (
            <section>
              <h3 className="text-sm font-bold text-slate-900">Horário</h3>
              {timeSlots.length > 0 ? (
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {timeSlots.map((slot) => {
                    const active = selectedSlot?.hora_inicio === slot.hora_inicio && selectedSlot?.hora_fim === slot.hora_fim;
                    return (
                      <button key={`${slot.hora_inicio}-${slot.hora_fim}`} type="button" onClick={() => setSelectedSlot(slot)} className={`rounded-xl border px-3 py-2 text-sm font-semibold ${active ? "border-pink-400 bg-pink-50" : "border-slate-300"}`}>
                        {slot.hora_inicio}–{slot.hora_fim}
                      </button>
                    );
                  })}
                </div>
              ) : <p className="mt-2 text-sm text-slate-500">Nenhum horário disponível para esta data.</p>}
            </section>
          )}

          {hasSelectableExtras && (
            <section className="rounded-2xl border border-slate-200 p-4">
              <div>
                <h3 className="font-bold text-slate-950">Adicionais opcionais</h3>
                <p className="mt-1 text-xs text-slate-500">Escolha somente o que você precisa. O total é recalculado pela AllePets.</p>
              </div>
              <div className="mt-3 space-y-2">
                {simpleOptions.map((option) => {
                  const rule = pricingConfig[option.key] as PricingRule | undefined;
                  return (
                    <label key={option.key} className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-slate-200 px-3 py-3">
                      <span className="flex gap-3">
                        <input type="checkbox" checked={simpleSelections[option.key]} onChange={() => setSimpleSelections((current) => ({ ...current, [option.key]: !current[option.key] }))} className="mt-1" />
                        <span>
                          <span className="block text-sm font-semibold">{option.label}</span>
                          <span className="block text-xs text-slate-500">{option.description}</span>
                        </span>
                      </span>
                      <strong className="whitespace-nowrap text-xs text-slate-700">{formatRule(rule)}</strong>
                    </label>
                  );
                })}

                {customExtras.map((extra) => (
                  <label key={extra.id} className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-slate-200 px-3 py-3">
                    <span className="flex gap-3">
                      <input type="checkbox" checked={selectedExtraIds.includes(extra.id)} onChange={() => toggleCustomExtra(extra.id)} className="mt-1" />
                      <span>
                        <span className="block text-sm font-semibold">{extra.name}</span>
                        <span className="block text-xs text-slate-500">Adicional oferecido por este anfitrião.</span>
                      </span>
                    </span>
                    <strong className="whitespace-nowrap text-xs text-slate-700">{extra.type === "percent" ? `+ ${extra.value}%` : `+ ${money(extra.value)}`}</strong>
                  </label>
                ))}

                {pricingConfig.extra_duration?.enabled && (
                  <label className="block rounded-xl border border-slate-200 px-3 py-3">
                    <span className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold">Tempo adicional</span>
                      <strong className="text-xs text-slate-700">{formatRule(pricingConfig.extra_duration)}</strong>
                    </span>
                    <select value={extraDurationMinutes} onChange={(event) => setExtraDurationMinutes(Number(event.target.value))} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                      <option value={0}>Não adicionar</option>
                      {[1, 2, 3, 4].map((amount) => {
                        const block = Math.max(1, numberValue(pricingConfig.extra_duration?.unit_minutes, 60));
                        return <option key={amount} value={amount * block}>{amount * block} minuto(s)</option>;
                      })}
                    </select>
                  </label>
                )}

                {pricingConfig.extra_stop?.enabled && (
                  <label className="block rounded-xl border border-slate-200 px-3 py-3">
                    <span className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold">Paradas adicionais</span>
                      <strong className="text-xs text-slate-700">+ {money(pricingConfig.extra_stop.value || 0)} por parada</strong>
                    </span>
                    <select value={extraStopCount} onChange={(event) => setExtraStopCount(Number(event.target.value))} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                      {[0, 1, 2, 3, 4].map((amount) => <option key={amount} value={amount}>{amount === 0 ? "Nenhuma" : `${amount} parada(s)`}</option>)}
                    </select>
                  </label>
                )}

                {pricingConfig.round_trip?.enabled && (
                  <label className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-slate-200 px-3 py-3">
                    <span className="flex items-center gap-3">
                      <input type="checkbox" checked={roundTrip} onChange={() => setRoundTrip((current) => !current)} />
                      <span className="text-sm font-semibold">Ida e volta</span>
                    </span>
                    <strong className="text-xs text-slate-700">+ {money(pricingConfig.round_trip.value || 0)}</strong>
                  </label>
                )}
              </div>
            </section>
          )}

          {(autoRules.length > 0 || pricingConfig.long_stay_discounts?.length || pricingConfig.distance?.enabled || pricingConfig.waiting?.enabled) && (
            <section className="rounded-2xl bg-slate-50 p-4">
              <h3 className="text-sm font-bold text-slate-900">Ajustes automáticos</h3>
              <p className="mt-1 text-xs text-slate-500">Você não precisa marcar estes itens. A AllePets aplica somente quando a condição ocorrer.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {autoRules.map((item) => <span key={item.label} className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold ring-1 ring-slate-200">{item.label} {formatRule(item.rule)}</span>)}
                {(pricingConfig.long_stay_discounts || []).filter((item) => numberValue(item?.discount_percent) > 0).map((item) => <span key={`long-${item.min_units}`} className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold ring-1 ring-slate-200">{item.min_units}+ noites: -{item.discount_percent}%</span>)}
                {pricingConfig.distance?.enabled && <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold ring-1 ring-slate-200">Distância calculada quando aplicável</span>}
                {pricingConfig.waiting?.enabled && <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold ring-1 ring-slate-200">Espera calculada quando aplicável</span>}
              </div>
            </section>
          )}

          {userId && (
            <section>
              <h3 className="text-sm font-bold text-slate-900">Para qual pet?</h3>
              {petsLoading ? <p className="mt-2 text-sm text-slate-500">Carregando seus pets...</p> : pets.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {pets.map((pet) => (
                    <label key={pet.id} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 px-3 py-3 text-sm">
                      <input type="checkbox" checked={selectedPetIds.includes(Number(pet.id))} onChange={() => togglePet(Number(pet.id))} />
                      <span>🐾 {pet.nome}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Cadastre um pet antes de concluir a reserva. <a className="font-bold underline" href={`/pets/novo?redirect=${encodeURIComponent(location.pathname + location.search)}`}>Cadastrar pet</a>
                </div>
              )}
            </section>
          )}

          <section className="rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-slate-950">Resumo da reserva</h3>
                <p className="text-xs text-slate-500">Valores finais são validados no servidor antes do pagamento.</p>
              </div>
              {quoteLoading && <span className="text-xs font-semibold text-slate-500">Recalculando...</span>}
            </div>

            {quoteLines.length > 0 ? (
              <div className="mt-3 divide-y divide-slate-100">
                {quoteLines.map((item, index) => (
                  <div key={`${item.code || "item"}-${index}`} className="flex items-center justify-between gap-4 py-2 text-sm">
                    <span className="text-slate-600">{item.label || "Item"}</span>
                    <strong className={numberValue(item.amount) < 0 ? "text-emerald-700" : "text-slate-900"}>{numberValue(item.amount) < 0 ? `- ${money(Math.abs(numberValue(item.amount)))}` : money(item.amount)}</strong>
                  </div>
                ))}
                {numberValue(quote?.tutor_fee) > 0 && <div className="flex items-center justify-between gap-4 py-2 text-sm"><span className="text-slate-600">Proteção AllePets</span><strong>{money(quote?.tutor_fee)}</strong></div>}
              </div>
            ) : (
              <div className="mt-3 text-sm text-slate-600">{!startDate ? "Escolha a data para calcular." : userId && selectedPetIds.length === 0 ? "Selecione pelo menos um pet para ver o total completo." : `Estimativa atual: ${money(displayedTotal)}`}</div>
            )}

            <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3">
              <span className="font-bold text-slate-900">Total</span>
              <strong className="text-lg" style={{ color: COLORS.primary }}>{startDate ? money(displayedTotal) : "—"}</strong>
            </div>
            {quoteError && <p className="mt-2 text-xs text-amber-700">{quoteError}</p>}
          </section>

          {checkoutError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{checkoutError}</div>}
        </div>

        <div className="border-t bg-white px-5 py-4 sm:px-6">
          {userId ? (
            <button type="button" disabled={checkoutLoading || !startDate || !selectedPetIds.length || (bathRequired && !modality) || (hourly && !selectedSlot)} onClick={checkout} className="w-full rounded-xl px-4 py-3 font-bold text-white disabled:cursor-not-allowed disabled:bg-slate-400" style={checkoutLoading ? undefined : { backgroundColor: COLORS.primary }}>
              {checkoutLoading ? "Preparando pagamento..." : "Confirmar e ir para pagamento"}
            </button>
          ) : (
            <a href={loginUrl} className="block w-full rounded-xl px-4 py-3 text-center font-bold text-white" style={{ backgroundColor: COLORS.primary }}>Entrar para reservar</a>
          )}
        </div>
      </div>
    </div>
  );
}
